import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  FileText, Download, AlertCircle, Calendar, 
  ChevronDown, DollarSign, TrendingUp, Receipt, Filter,
  FileSpreadsheet, Loader2, CheckCircle, Clock, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { HostAppShell } from '../components/HostAppShell';
import { HostDirectModeBanner } from '../components/HostDirectModeBanner';

/**
 * HostStatements - Enhanced host financial statements page
 * Features: Full fee breakdown, filters, CSV + PDF export, payout tracking
 * HOST ONLY - Platform owners are blocked
 */
const HostStatements = () => {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [statementData, setStatementData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingCSV, setDownloadingCSV] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  
  // Filters
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [selectedPayoutStatus, setSelectedPayoutStatus] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Generate month options (last 24 months)
  const monthOptions = React.useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  }, []);

  useEffect(() => {
    fetchStatement();
  }, [selectedMonth, selectedVehicle, selectedPayoutStatus]);

  const fetchStatement = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ month: selectedMonth });
      if (selectedVehicle) params.append('vehicle_id', selectedVehicle);
      if (selectedPayoutStatus) params.append('payout_status', selectedPayoutStatus);
      
      const res = await api.get(`/host/statements?${params}`);
      setStatementData(res.data);
    } catch (error) {
      console.error('Error fetching statement:', error);
      if (error.response?.status === 403) {
        // Access denied - probably platform owner
      } else {
        toast.error('Failed to load statement');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    setDownloadingCSV(true);
    try {
      const params = new URLSearchParams({ month: selectedMonth });
      if (selectedVehicle) params.append('vehicle_id', selectedVehicle);
      if (selectedPayoutStatus) params.append('payout_status', selectedPayoutStatus);
      
      const response = await api.get(`/host/statements.csv?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `hirehub_statement_${selectedMonth}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV statement downloaded');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast.error('Failed to download CSV');
    } finally {
      setDownloadingCSV(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const params = new URLSearchParams({ month: selectedMonth });
      if (selectedVehicle) params.append('vehicle_id', selectedVehicle);
      if (selectedPayoutStatus) params.append('payout_status', selectedPayoutStatus);
      
      const response = await api.get(`/host/statements.pdf?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `hirehub_statement_${selectedMonth}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF statement downloaded');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const getPayoutStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-700 border-green-200">Paid</Badge>;
      case 'eligible':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Ready</Badge>;
      case 'not_eligible':
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Pending</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700 border-red-200">Failed</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  // Block platform owners
  if (user?.is_platform_owner) {
    return (
      <HostAppShell title="Statements" activeId="statements">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Access Denied</h2>
            <p className="text-slate-500 mb-4">Platform owners access financial reports via the Platform Owner dashboard.</p>
            <Button onClick={() => navigate('/platform-owner')}>Go to Platform Owner Dashboard</Button>
          </CardContent>
        </Card>
      </HostAppShell>
    );
  }

  return (
    <HostAppShell title="Statements" activeId="statements">
      <div className="max-w-5xl mx-auto space-y-6">
        <HostDirectModeBanner variant="subtle" context="statements" />
        {/* Controls Bar */}
        <Card className="border-slate-100">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              {/* Month Selector */}
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-[#004B8D] hidden sm:block" />
                <div className="relative">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="appearance-none bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 pr-10 font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#004B8D] text-sm"
                    data-testid="month-selector"
                  >
                    {monthOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                
                {/* Filter Toggle */}
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="text-slate-600"
                >
                  <Filter className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Filters</span>
                </Button>
              </div>
              
              {/* Download Buttons */}
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadCSV}
                  disabled={downloadingCSV || loading || !statementData?.line_items?.length}
                  data-testid="download-csv-btn"
                >
                  {downloadingCSV ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                  )}
                  CSV
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadPDF}
                  disabled={downloadingPDF || loading || !statementData?.line_items?.length}
                  data-testid="download-pdf-btn"
                >
                  {downloadingPDF ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2" />
                  )}
                  PDF
                </Button>
              </div>
            </div>
            
            {/* Filters Panel */}
            {showFilters && statementData?.filters && (
              <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-4">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Vehicle</label>
                  <select
                    value={selectedVehicle}
                    onChange={(e) => setSelectedVehicle(e.target.value)}
                    className="appearance-none bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm cursor-pointer"
                    data-testid="vehicle-filter"
                  >
                    <option value="">All Vehicles</option>
                    {statementData.filters.vehicles?.map((v) => (
                      <option key={v.vehicle_id} value={v.vehicle_id}>{v.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Payout Status</label>
                  <select
                    value={selectedPayoutStatus}
                    onChange={(e) => setSelectedPayoutStatus(e.target.value)}
                    className="appearance-none bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm cursor-pointer"
                    data-testid="payout-filter"
                  >
                    <option value="">All Statuses</option>
                    <option value="paid">Paid</option>
                    <option value="eligible">Ready for Payout</option>
                    <option value="not_eligible">Pending</option>
                  </select>
                </div>
                {(selectedVehicle || selectedPayoutStatus) && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => { setSelectedVehicle(''); setSelectedPayoutStatus(''); }}
                    className="self-end"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#004B8D]" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            {statementData?.summary && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="summary-cards">
                <Card className="border-slate-100">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 mb-1">Gross Earnings</p>
                    <p className="text-lg sm:text-xl font-bold text-slate-900">
                      ${statementData.summary.gross_earnings?.toFixed(2) || '0.00'}
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-slate-100">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 mb-1">Platform Fee</p>
                    <p className="text-lg sm:text-xl font-bold text-red-600">
                      -${statementData.summary.platform_fees?.toFixed(2) || '0.00'}
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-slate-100">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 mb-1">Processing Fee</p>
                    <p className="text-lg sm:text-xl font-bold text-red-600">
                      -${(statementData.summary.processing_fees ?? statementData.summary.stripe_fees)?.toFixed(2) || '0.00'}
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-green-50 bg-green-50/50">
                  <CardContent className="p-4">
                    <p className="text-xs text-green-600 mb-1">Net Earnings</p>
                    <p className="text-lg sm:text-xl font-bold text-green-700">
                      ${statementData.summary.net_earnings?.toFixed(2) || '0.00'}
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-slate-100">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Pending
                    </p>
                    <p className="text-lg sm:text-xl font-bold text-amber-600">
                      ${statementData.summary.pending_payout?.toFixed(2) || '0.00'}
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-slate-100">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Paid
                    </p>
                    <p className="text-lg sm:text-xl font-bold text-slate-900">
                      ${statementData.summary.paid_this_month?.toFixed(2) || '0.00'}
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Line Items Table */}
            <Card className="border-slate-100" data-testid="line-items-card">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading flex items-center gap-2 text-lg">
                  <Receipt className="w-5 h-5 text-slate-600" />
                  Booking Details
                  {statementData?.summary?.bookings_count > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {statementData.summary.bookings_count} bookings
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!statementData?.line_items?.length ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No bookings this month</p>
                    <p className="text-sm text-slate-400 mt-1">Complete bookings to see them here</p>
                  </div>
                ) : (
                  <>
                    {/* Desktop Table */}
                    <div className="hidden lg:block overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-100">
                            <th className="text-left py-3 px-2 font-medium text-slate-500">Booking</th>
                            <th className="text-left py-3 px-2 font-medium text-slate-500">Vehicle</th>
                            <th className="text-left py-3 px-2 font-medium text-slate-500">Renter</th>
                            <th className="text-left py-3 px-2 font-medium text-slate-500">Dates</th>
                            <th className="text-right py-3 px-2 font-medium text-slate-500">Gross</th>
                            <th className="text-right py-3 px-2 font-medium text-slate-500">Platform</th>
                            <th className="text-right py-3 px-2 font-medium text-slate-500">Fees</th>
                            <th className="text-right py-3 px-2 font-medium text-slate-500">Net</th>
                            <th className="text-center py-3 px-2 font-medium text-slate-500">Payout</th>
                          </tr>
                        </thead>
                        <tbody>
                          {statementData.line_items.map((item, index) => (
                            <tr 
                              key={item.booking_id || index} 
                              className="border-b border-slate-50 hover:bg-slate-50"
                            >
                              <td className="py-3 px-2">
                                <span className="font-mono text-xs">{item.booking_id?.slice(0, 12)}</span>
                                {!item.has_complete_data && (
                                  <span title="Legacy booking - fees may be incomplete" className="ml-1">
                                    <AlertTriangle className="w-3 h-3 inline text-amber-500" />
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-2 max-w-[120px] truncate">{item.vehicle}</td>
                              <td className="py-3 px-2 max-w-[100px] truncate">{item.renter_name}</td>
                              <td className="py-3 px-2 text-slate-500 text-xs">{item.dates}</td>
                              <td className="py-3 px-2 text-right">${item.gross?.toFixed(2)}</td>
                              <td className="py-3 px-2 text-right text-red-600">-${item.platform_fee?.toFixed(2)}</td>
                              <td className="py-3 px-2 text-right text-red-600">
                                {(item.processing_fee ?? item.stripe_fee) > 0 ? `-$${(item.processing_fee ?? item.stripe_fee)?.toFixed(2)}` : '-'}
                              </td>
                              <td className="py-3 px-2 text-right font-medium text-green-600">${item.net?.toFixed(2)}</td>
                              <td className="py-3 px-2 text-center">
                                {getPayoutStatusBadge(item.payout_status)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-slate-50 font-medium">
                            <td colSpan="4" className="py-3 px-2">Total</td>
                            <td className="py-3 px-2 text-right">${statementData.summary.gross_earnings?.toFixed(2)}</td>
                            <td className="py-3 px-2 text-right text-red-600">-${statementData.summary.platform_fees?.toFixed(2)}</td>
                            <td className="py-3 px-2 text-right text-red-600">-${(statementData.summary.processing_fees ?? statementData.summary.stripe_fees)?.toFixed(2)}</td>
                            <td className="py-3 px-2 text-right text-green-600">${statementData.summary.net_earnings?.toFixed(2)}</td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    {/* Tablet Table (Simplified) */}
                    <div className="hidden sm:block lg:hidden overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-100">
                            <th className="text-left py-3 px-2 font-medium text-slate-500">Booking</th>
                            <th className="text-left py-3 px-2 font-medium text-slate-500">Vehicle</th>
                            <th className="text-right py-3 px-2 font-medium text-slate-500">Gross</th>
                            <th className="text-right py-3 px-2 font-medium text-slate-500">Fees</th>
                            <th className="text-right py-3 px-2 font-medium text-slate-500">Net</th>
                            <th className="text-center py-3 px-2 font-medium text-slate-500">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {statementData.line_items.map((item, index) => (
                            <tr key={item.booking_id || index} className="border-b border-slate-50">
                              <td className="py-3 px-2">
                                <span className="font-mono text-xs">{item.booking_id?.slice(0, 8)}</span>
                              </td>
                              <td className="py-3 px-2 max-w-[100px] truncate text-xs">{item.vehicle}</td>
                              <td className="py-3 px-2 text-right">${item.gross?.toFixed(2)}</td>
                              <td className="py-3 px-2 text-right text-red-600">
                                -${(item.platform_fee + (item.processing_fee ?? item.stripe_fee ?? 0)).toFixed(2)}
                              </td>
                              <td className="py-3 px-2 text-right font-medium text-green-600">${item.net?.toFixed(2)}</td>
                              <td className="py-3 px-2 text-center">{getPayoutStatusBadge(item.payout_status)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile List */}
                    <div className="sm:hidden space-y-3">
                      {statementData.line_items.map((item, index) => (
                        <div 
                          key={item.booking_id || index}
                          className="bg-slate-50 rounded-lg p-4"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{item.vehicle}</p>
                              <p className="text-xs text-slate-500">{item.renter_name}</p>
                              <p className="text-xs text-slate-400">{item.dates}</p>
                            </div>
                            {getPayoutStatusBadge(item.payout_status)}
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-sm mt-3 pt-2 border-t border-slate-200">
                            <div>
                              <p className="text-xs text-slate-500">Gross</p>
                              <p className="font-medium">${item.gross?.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Fees</p>
                              <p className="font-medium text-red-600">
                                -${(item.platform_fee + (item.processing_fee ?? item.stripe_fee ?? 0)).toFixed(2)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Net</p>
                              <p className="font-medium text-green-600">${item.net?.toFixed(2)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Footer Note */}
            <p className="text-xs text-slate-400 text-center">
              Statements are generated from your booking data. For official tax documents or questions, contact support@hirehubaustralia.com.
            </p>
          </>
        )}
      </div>
    </HostAppShell>
  );
};

export default HostStatements;
